import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, Loader2 } from 'lucide-react';
import { Card } from './Card';
import { AiOutput, type AiCitation } from './AiOutput';
import { api } from '../../lib/api';
import { useCan } from '../../hooks/useCan';

interface AgentRunResponse {
  runId: string;
  output: string;
  model: string;
  costCents: number;
  citations: AiCitation[];
}

/**
 * A compact, reusable "Ask the AI" slot. Runs a single, page-appropriate
 * agent against the workspace and renders the answer through `<AiOutput>` —
 * formatted markdown, citation pills (deep-linked to the source record) and a
 * copy button. This is how every domain page that previously showed no AI text
 * gets a real, beautiful AI surface without re-implementing the agent UI.
 */
export function AiInsightCard({
  agent,
  title,
  prompt,
  promptLabel,
  targetKind,
  targetId,
  tone,
}: {
  /** Agent kind to run (e.g. 'close_coach', 'cost_reduction'). */
  agent: string;
  /** Card heading. */
  title: string;
  /** Default prompt seeded into the textarea. */
  prompt: string;
  /** Optional label above the prompt box. */
  promptLabel?: string;
  targetKind?: string;
  targetId?: string;
  /** Force a callout tone on the rendered answer. */
  tone?: 'neutral' | 'note' | 'recommendation' | 'warning';
}) {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const { canMutate } = useCan();
  const [userPrompt, setUserPrompt] = useState(prompt);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation<AgentRunResponse>({
    mutationFn: async () =>
      (
        await api.post(`/workspaces/${wsId}/agents/run`, {
          agent,
          userPrompt,
          targetKind,
          targetId,
        })
      ).data,
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? t('aiOutput.runFailed')),
    onSuccess: () => setError(null),
  });

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-accent-50 dark:bg-accent-900/30 text-accent-600">
          <Sparkles size={15} />
        </span>
        <div>
          <div className="font-medium text-sm">{title}</div>
          <div className="text-[11px] text-ink-500">{t(`agents.kinds.${agent}`, agent)}</div>
        </div>
      </div>

      {canMutate ? (
        <>
          {promptLabel && (
            <label className="block text-[11px] uppercase tracking-wide text-ink-500 mb-1">{promptLabel}</label>
          )}
          <textarea
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
          />
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={run.isPending || !userPrompt.trim()}
            className="mt-2.5 inline-flex items-center gap-2 rounded-lg font-medium h-9 px-4 text-sm bg-accent-600 text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {run.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {t('aiOutput.ask')}
          </button>
        </>
      ) : (
        <div className="text-xs text-ink-500">{t('common.viewerReadOnly')}</div>
      )}

      {error && (
        <div className="mt-3 text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
          {error}
        </div>
      )}

      {run.data?.output && (
        <div className="mt-3 rounded-lg bg-ink-50 dark:bg-ink-900 p-3">
          <AiOutput citations={run.data.citations} tone={tone}>
            {run.data.output}
          </AiOutput>
        </div>
      )}
    </Card>
  );
}
